import { useEffect, useRef } from "react";
import { projector } from "@/lib/geo";
import { modisUrl } from "@/lib/api";

const LABEL_FILL = {
  PRIME: "0,255,135",
  GOOD: "52,211,153",
  FAIR: "255,209,102",
  POOR: "100,116,139",
  AVOID: "239,68,68",
};

export const CATCH_STYLE = {
  good_fish: { c: "#22c55e", label: "FISH" },
  jellyfish: { c: "#a855f7", label: "JELLY" },
  sargassum: { c: "#f59e0b", label: "WEED" },
  empty: { c: "#64748b", label: "EMPTY" },
  other: { c: "#38bdf8", label: "OTHER" },
};

export default function FishingMap({ advisory, islands = [], placed, onPlace }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const stateRef = useRef({ proj: null, W: 0, H: 0, bbox: null });

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = modisUrl({ width: 1024, height: 600 });
    img.onload = () => (imgRef.current = img);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !advisory) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const bbox = advisory.bbox;
    const [w, s, e, n] = bbox;
    const res = advisory.res || 0.5;
    let W = 0, H = 0, proj, raf, t = 0;

    const resize = () => {
      const r = wrap.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + "px"; canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      proj = projector(bbox, W, H);
      Object.assign(stateRef.current, { proj, W, H, bbox });
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const cw = (W2) => (W2 * res) / (e - w);
    const ch = (H2) => (H2 * res) / (n - s);

    const draw = () => {
      t += 1;
      const pulse = 0.5 + 0.5 * Math.sin(t * 0.06);
      // deep ocean base + dimmed MODIS backdrop for real-world context
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, "#04122c"); g.addColorStop(1, "#02081a");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      if (imgRef.current) {
        ctx.globalAlpha = 0.28;
        ctx.drawImage(imgRef.current, 0, 0, W, H);
        ctx.globalAlpha = 1;
      }

      const cwPx = cw(W) + 1, chPx = ch(H) + 1;
      for (const c of advisory.cells) {
        const x = proj.x(c.lng), y = proj.y(c.lat);
        const fill = LABEL_FILL[c.label] || LABEL_FILL.POOR;
        let a;
        if (c.label === "AVOID") a = 0.16 + c.risk * 0.5;
        else if (c.label === "PRIME" || c.label === "GOOD") a = 0.12 + c.opportunity * 0.5;
        else if (c.label === "FAIR") a = 0.12;
        else a = 0.05;
        ctx.fillStyle = `rgba(${fill},${a})`;
        ctx.fillRect(x - cwPx / 2, y - chPx / 2, cwPx, chPx);
      }

      // island landmasses (context)
      for (const isl of islands) {
        const x = proj.x(isl.lng), y = proj.y(isl.lat);
        if (x < -10 || x > W + 10 || y < -10 || y > H + 10) continue;
        ctx.fillStyle = "#1f6f47";
        ctx.strokeStyle = "rgba(217,201,163,0.7)";
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(x, y, 3.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.font = "600 9px 'JetBrains Mono', monospace";
        ctx.fillStyle = "rgba(226,232,240,0.65)";
        ctx.fillText(isl.name, x + 6, y + 3);
      }

      // recommended fishing zones (star)
      for (const z of advisory.good_zones || []) {
        const x = proj.x(z.lng), y = proj.y(z.lat);
        ctx.strokeStyle = `rgba(0,255,135,${0.5 + 0.4 * pulse})`;
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(x, y, 9 + pulse * 4, 0, Math.PI * 2); ctx.stroke();
        star(ctx, x, y, 5, "#00ff87");
      }
      // avoid zones (cross)
      for (const z of advisory.avoid_zones || []) {
        const x = proj.x(z.lng), y = proj.y(z.lat);
        ctx.strokeStyle = `rgba(239,68,68,${0.55 + 0.35 * pulse})`;
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.moveTo(x - 5, y - 5); ctx.lineTo(x + 5, y + 5);
        ctx.moveTo(x + 5, y - 5); ctx.lineTo(x - 5, y + 5); ctx.stroke();
      }

      // crowdsourced fisher reports
      for (const r of advisory.reports || []) {
        const st = CATCH_STYLE[r.catch_type] || CATCH_STYLE.other;
        const x = proj.x(r.lng), y = proj.y(r.lat);
        ctx.fillStyle = st.c;
        ctx.strokeStyle = "rgba(2,8,26,0.9)";
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      }

      // pending report location
      if (placed) {
        const x = proj.x(placed.lng), y = proj.y(placed.lat);
        ctx.strokeStyle = `rgba(0,229,255,${0.6 + 0.4 * pulse})`;
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(x, y, 10 + pulse * 6, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x - 12, y); ctx.lineTo(x + 12, y);
        ctx.moveTo(x, y - 12); ctx.lineTo(x, y + 12); ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [advisory, islands, placed]);

  const handleClick = (ev) => {
    const { proj, bbox } = stateRef.current;
    if (!proj || !bbox) return;
    const [w, s, e, n] = bbox;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = ev.clientX - rect.left, my = ev.clientY - rect.top;
    const lng = w + (mx / rect.width) * (e - w);
    const lat = n - (my / rect.height) * (n - s);
    onPlace?.({ lat: Math.round(lat * 1000) / 1000, lng: Math.round(lng * 1000) / 1000 });
  };

  return (
    <div ref={wrapRef} className="relative h-full w-full" data-testid="fishing-map">
      <canvas ref={canvasRef} onClick={handleClick} className="h-full w-full cursor-crosshair" />
      <div className="pointer-events-none absolute bottom-2 left-2 rounded-md border border-white/10 bg-black/55 px-2 py-1 backdrop-blur">
        <span className="eyebrow text-cyan-300/80">Tap the map to log your catch location</span>
      </div>
    </div>
  );
}

function star(ctx, cx, cy, r, color) {
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const ang = (Math.PI / 5) * i - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r * 0.45;
    const x = cx + Math.cos(ang) * rad, y = cy + Math.sin(ang) * rad;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath(); ctx.fill();
}
